import { useTranslation } from 'react-i18next';
import { Loader2, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';
import type { DisruptionPnrGroupRow } from '@/lib/disruptions/types';
import { toneClasses } from './airport-tokens';
import { callbackRequested, phoneOnFile } from './pnr-steps';

// «Prefiero que me llamen» (accepted-offer-recovery §6.7): the passenger declined and asked for
// a call. An attention line wherever the passenger's answer shows, with the call when a phone is
// on file — else the contact fix. Renders nothing for any other answer.

export function CallbackRequest({
  group,
  formatTime,
  onCall,
  onContact,
  calling = false,
}: {
  group: DisruptionPnrGroupRow;
  formatTime?: ((iso: string) => string) | undefined;
  onCall?: (() => void) | undefined;
  onContact?: (() => void) | undefined;
  calling?: boolean;
}) {
  const { t } = useTranslation('pnr');
  const { t: tActions } = useTranslation('pnrActions');
  if (!callbackRequested(group)) return null;
  const at = group.lastOfferResponse?.at;
  const phone = phoneOnFile(group);
  const tone = toneClasses('warn');
  return (
    <div
      className={cn('flex flex-wrap items-center justify-between gap-2 rounded-lg border px-3 py-2', tone.surface, tone.border)}
      role="status"
      data-testid="pnr-callback-request"
    >
      <span className="text-sm">
        <b className={tone.text}>{t('card.talk.callback')}</b>
        {at && formatTime ? <span className="ml-2 tabular-nums text-muted-foreground">{formatTime(at)}</span> : null}
        {!phone ? <span className="block text-[13px] text-muted-foreground">{t('card.talk.callbackNoPhone')}</span> : null}
      </span>
      {phone && onCall ? (
        <Button type="button" size="sm" className="h-9" onClick={onCall} disabled={calling} data-testid="pnr-callback-call">
          {calling ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Phone className="h-4 w-4" aria-hidden />}
          {tActions('utility.call.label')}
        </Button>
      ) : !phone && onContact ? (
        <Button type="button" size="sm" variant="outline" className="h-9" onClick={onContact} data-testid="pnr-callback-contact">
          {tActions('utility.updateContact')}
        </Button>
      ) : null}
    </div>
  );
}
